// ─────────────────────────────────────────────────────────────────────────
// PRICE INTEGRITY PAGE  (Auto Trading → Price Integrity tab)
//
// Two views:
//  1) UNIVERSE SCAN (default) — every flag across the active-strategy universe
//     in one snapshot, so the human can triage all suspect names at once.
//     Read from a persisted S3 snapshot; an admin can trigger a rescan.
//  2) SINGLE-TICKER REVIEW — type a ticker, get its divergence + impossible-gap
//     flags live (one vendor fetch). The drill-down tool.
//
// Divergence (primary) = our split-only adjusted series vs vendor split-adjusted
// close, compared by daily return. Impossible gap (backstop) = a ≥50% one-day
// move on our series alone. Every flag tagged on_action vs orphan.
//
// No writes to price data. Mobile-first, dark-default, institutional styling.
// ─────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { tradingService } from '../../services/tradingService';
import { useAuth } from '../../context/AuthContext';

const pct = (r) => (r === null || r === undefined ? '—' : `${(r * 100).toFixed(2)}%`);
const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');
const fmtTs = (t) => {
  if (!t) return '—';
  const d = new Date(t);
  return isNaN(d.getTime()) ? String(t) : d.toLocaleString();
};

// green for up moves, red for down — consistent with the rest of the portal.
const moveColor = (r) =>
  r === null || r === undefined
    ? 'text-gray-400'
    : r >= 0
    ? 'text-green-500'
    : 'text-red-500';

const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toFixed(2)}`);
const ratioX = (v) => (v === null || v === undefined ? '—' : `${Number(v).toFixed(2)}×`);

// Nearest corporate action to a drift day + its nature per BOTH vendors (ours vs
// yfinance, compared on the common raw as-paid basis — yf amounts already lifted by the
// backend). Colour encodes agreement: gray = agree, amber = amounts disagree/ambiguous,
// blue = ours-only, red = yfinance-only, purple = spinoff.
const fmtCa = (ca) => {
  if (!ca) return { text: '—', cls: 'text-gray-300 dark:text-gray-600' };
  const { kind, status, our, yf, date } = ca;
  const near = fmtDate(date);
  if (kind === 'spinoff') return { text: `Spinoff ${money(our)} · ${near}`, cls: 'text-purple-500' };
  const val = kind === 'split' ? ratioX : money;
  const noun = kind === 'split' ? 'Split' : 'Div';
  if (status === 'match') return { text: `${noun} ${val(our)} = yf ${val(yf)}`, cls: 'text-gray-500 dark:text-gray-400' };
  if (status === 'mismatch') return { text: `${noun} ${val(our)} ≠ yf ${val(yf)}`, cls: 'text-amber-500' };
  if (status === 'ambiguous') return { text: `${noun} ${val(our)} · ambiguous`, cls: 'text-amber-500' };
  if (status === 'our_only') return { text: `${noun} ${val(our)} · yf missing`, cls: 'text-blue-500' };
  if (status === 'yf_only') return { text: `yf ${noun} ${val(yf)} · ours missing`, cls: 'text-red-500' };
  return { text: `${noun} ${near}`, cls: 'text-gray-400' };
};

// Cumulative drift: the slow same-sign bias the per-day check is blind to. Verdict is
// the STAIRCASE on how far OUR window return diverges from the SAME vendor's own —
// both relatively (ratio_drift) and absolutely (cum_diff). Never annualized.
// We show the signed cumulative gap, with the relative ratio in parentheses when large.
const fmtDrift = (d) => {
  if (!d || !d.basis || d.basis === 'insufficient') return '—';
  if (d.cum_drift !== null && d.cum_drift !== undefined) {
    const rel =
      d.ratio_drift !== null && d.ratio_drift !== undefined
        ? ` (${(d.ratio_drift * 100).toFixed(2)}%)`
        : '';
    return `${(d.cum_drift * 100).toFixed(2)}% cum${rel}`;
  }
  return '—';
};
const driftMag = (d) => {
  if (!d || !d.basis || d.basis === 'insufficient') return -1;
  // Rank by absolute cumulative-return points (cum_diff) — the staircase's primary axis.
  if (d.cum_diff !== null && d.cum_diff !== undefined) return Math.abs(d.cum_diff);
  if (d.cum_drift !== null && d.cum_drift !== undefined) return Math.abs(d.cum_drift);
  return -1;
};
const driftSign = (d) => {
  if (!d || !d.basis || d.basis === 'insufficient') return null;
  return d.cum_drift;
};

// Pull the flat per-security drift columns (from the scan roster) back into the same
// {basis, ratio_drift, cum_diff, ann_drift, cum_drift, exceeds, years} shape the
// single-ticker report carries.
const driftFromRow = (row, prefix) =>
  row[`${prefix}drift_basis`] === undefined
    ? null
    : {
        basis: row[`${prefix}drift_basis`],
        ratio_drift: row[`${prefix}drift_ratio`],
        cum_diff: row[`${prefix}drift_diff`],
        ann_drift: row[`${prefix}drift_ann`],
        cum_drift: row[`${prefix}drift_cum`],
        years: row[`${prefix}drift_years`],
        exceeds: row[`${prefix}drift_exceeds`],
      };

function DriftBadge({ drift }) {
  if (!drift || !drift.basis || drift.basis === 'insufficient') {
    return (
      <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-gray-500/15 text-gray-400">
        No window
      </span>
    );
  }
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${
        drift.exceeds ? 'bg-amber-500/15 text-amber-500' : 'bg-green-500/15 text-green-500'
      }`}
    >
      {drift.exceeds ? 'Drift' : 'No drift'}
    </span>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0d1117] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-sm font-mono mt-0.5 ${accent || 'text-gray-900 dark:text-gray-100'}`}>{value}</div>
    </div>
  );
}

function TagBadge({ onAction }) {
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${
        onAction ? 'bg-blue-500/15 text-blue-400' : 'bg-gray-500/15 text-gray-400'
      }`}
    >
      {onAction ? 'On action' : 'Orphan'}
    </span>
  );
}

// Impossible-gap cross-check: was the SAME gap present in the vendor's own series?
//   both          → vendor saw the same gap (advisory: still read the tag — a shared
//                   error like a spinoff-as-split shows in both yet both are wrong)
//   ours_only     → vendor has data here but no gap → likely our mechanical residual
//   vendor_missing→ no vendor bar on this date (incomplete legacy history) — can't tell
function VendorCheckBadge({ check }) {
  const map = {
    both: { cls: 'bg-green-500/15 text-green-500', label: 'In both' },
    ours_only: { cls: 'bg-red-500/15 text-red-500', label: 'Ours only' },
    vendor_missing: { cls: 'bg-gray-500/15 text-gray-400', label: 'Vendor n/a' },
  };
  const m = map[check] || map.vendor_missing;
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${m.cls}`}>{m.label}</span>
  );
}

// Divergence table — our return vs the vendor's on the same day.
// `showTicker` adds a leading ticker column for the universe view.
function DivergenceTable({ flags, showTicker }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[520px] text-left">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {showTicker && <th className="px-2 py-2 font-normal">Ticker</th>}
            <th className="px-2 py-2 font-normal">Date</th>
            <th className="px-2 py-2 font-normal text-right">Our move</th>
            <th className="px-2 py-2 font-normal text-right">Vendor move</th>
            <th className="px-2 py-2 font-normal text-right">Diff</th>
            <th className="px-2 py-2 font-normal text-center">Tag</th>
          </tr>
        </thead>
        <tbody>
          {flags.map((f, i) => (
            <tr key={`${f.ticker || ''}-${f.date}-d-${i}`} className="border-t border-gray-100 dark:border-gray-800/60">
              {showTicker && (
                <td className="px-2 py-1.5 font-mono text-xs font-medium text-gray-900 dark:text-gray-100">{f.ticker}</td>
              )}
              <td className="px-2 py-1.5 font-mono text-xs text-gray-700 dark:text-gray-300">{fmtDate(f.date)}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(f.ret)}`}>{pct(f.ret)}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(f.ret_vendor)}`}>{pct(f.ret_vendor)}</td>
              <td className="px-2 py-1.5 font-mono text-xs text-right text-gray-500 dark:text-gray-400">{pct(f.diff)}</td>
              <td className="px-2 py-1.5 text-center"><TagBadge onAction={f.on_action} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Impossible-gap table — a ≥50% one-day move on our series alone, with the vendor
// cross-check (is the same gap in the vendor's series too?) front and centre.
function ImpossibleGapTable({ flags, showTicker }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[520px] text-left">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {showTicker && <th className="px-2 py-2 font-normal">Ticker</th>}
            <th className="px-2 py-2 font-normal">Date</th>
            <th className="px-2 py-2 font-normal text-right">Our move</th>
            <th className="px-2 py-2 font-normal text-right">Vendor move</th>
            <th className="px-2 py-2 font-normal text-center">In vendor?</th>
            <th className="px-2 py-2 font-normal text-center">Tag</th>
          </tr>
        </thead>
        <tbody>
          {flags.map((f, i) => (
            <tr key={`${f.ticker || ''}-${f.date}-g-${i}`} className="border-t border-gray-100 dark:border-gray-800/60">
              {showTicker && (
                <td className="px-2 py-1.5 font-mono text-xs font-medium text-gray-900 dark:text-gray-100">{f.ticker}</td>
              )}
              <td className="px-2 py-1.5 font-mono text-xs text-gray-700 dark:text-gray-300">{fmtDate(f.date)}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(f.ret)}`}>{pct(f.ret)}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(f.vendor_ret)}`}>{pct(f.vendor_ret)}</td>
              <td className="px-2 py-1.5 text-center"><VendorCheckBadge check={f.vendor_check} /></td>
              <td className="px-2 py-1.5 text-center"><TagBadge onAction={f.on_action} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Split a flat flag list into its two kinds.
const splitKinds = (flags) => ({
  divergences: (flags || []).filter((f) => f.source === 'divergence'),
  gaps: (flags || []).filter((f) => f.source === 'impossible_gap'),
});

function Chevron({ open }) {
  return (
    <svg
      className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M7 5l6 5-6 5V5z" />
    </svg>
  );
}

function CleanBadge({ clean }) {
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${
        clean ? 'bg-green-500/15 text-green-500' : 'bg-amber-500/15 text-amber-500'
      }`}
    >
      {clean ? 'Clean' : 'Flagged'}
    </span>
  );
}

// Generic expand/collapse tile. `accent` colours the title; `badge` sits on the right.
function Collapsible({ title, count, accent, defaultOpen = false, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0d1117] overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
      >
        <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <Chevron open={open} />
          <span className={`text-[11px] uppercase tracking-wider font-medium ${accent || 'text-gray-700 dark:text-gray-200'}`}>
            {title}
          </span>
          {count !== null && count !== undefined && (
            <span className="text-[11px] text-gray-400 dark:text-gray-500">({count})</span>
          )}
        </span>
        {badge}
      </button>
      {open && <div className="px-3 pb-3 pt-0.5">{children}</div>}
    </div>
  );
}

const Empty = ({ children }) => (
  <div className="text-center text-xs text-gray-500 dark:text-gray-400 py-3">{children}</div>
);

// One comparison MODULE (split-adjusted OR total-return): a top-level collapsible
// holding two nested collapsibles — Divergences and Impossible gaps.
function ComparisonModule({ title, subtitle, flags, showTicker, defaultOpen = false }) {
  const { divergences, gaps } = splitKinds(flags);
  const total = divergences.length + gaps.length;
  return (
    <Collapsible
      title={title}
      count={total}
      accent="text-gray-900 dark:text-gray-100"
      defaultOpen={defaultOpen}
      badge={<CleanBadge clean={total === 0} />}
    >
      {subtitle && <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">{subtitle}</div>}
      <div className="space-y-2">
        <Collapsible
          title="Divergences"
          count={divergences.length}
          accent="text-amber-500"
          defaultOpen={divergences.length > 0}
        >
          {divergences.length > 0 ? (
            <DivergenceTable flags={divergences} showTicker={showTicker} />
          ) : (
            <Empty>No divergences — our return matches the vendor's across the window.</Empty>
          )}
        </Collapsible>
        <Collapsible
          title="Impossible gaps"
          count={gaps.length}
          accent="text-purple-400"
          defaultOpen={gaps.length > 0}
        >
          {gaps.length > 0 ? (
            <ImpossibleGapTable flags={gaps} showTicker={showTicker} />
          ) : (
            <Empty>No impossible gaps — no ≥50% one-day move on our series alone.</Empty>
          )}
        </Collapsible>
      </div>
    </Collapsible>
  );
}

// The pair of modules: split-adjusted (workhorse) + total-return (dividend check).
// `splitFlags` / `trFlags` are flat flag lists; `trAvailable` hides the TR module
// for snapshots produced before the total-return scan existed.
function ComparisonModules({ splitFlags, trFlags, trAvailable, showTicker }) {
  return (
    <div className="space-y-2">
      <ComparisonModule
        title="Split-adjusted"
        subtitle="Our split-only series vs the vendor's split-adjusted close (dividends excluded). The workhorse — catches a wrong/missing split or spinoff factor."
        flags={splitFlags}
        showTicker={showTicker}
        defaultOpen
      />
      {trAvailable ? (
        <ComparisonModule
          title="Total return"
          subtitle="Our split+dividend series vs the vendor's adjusted close (adjClose). The dividend cross-check — catches a missing/wrong dividend, an adjDividend leak, or a special distribution we never sourced."
          flags={trFlags}
          showTicker={showTicker}
        />
      ) : (
        <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-800 px-3 py-3 text-[11px] text-gray-500 dark:text-gray-400">
          Total-return comparison not in this snapshot yet — run a rescan to populate it.
        </div>
      )}
    </div>
  );
}

// Drift-attribution regime: the verdict on WHERE the cumulative drift accumulated.
//   concentrated     → a few dates carry it → a phantom/mis-typed CA or a handful of
//                      mis-stated dividends. The most actionable — open those dates.
//   diffuse_dividend → spread, but clustered on dividend ex-dates beyond fair share →
//                      systematic dividend mishandling (the diffuse-but-real loophole).
//   diffuse          → spread evenly, dividends not over-represented → benign vendor
//                      convention noise (or a single orphan raw-bar glitch).
const REGIME = {
  concentrated: { cls: 'bg-red-500/15 text-red-500', label: 'Concentrated' },
  diffuse_dividend: { cls: 'bg-purple-500/15 text-purple-400', label: 'Dividend-wide' },
  diffuse: { cls: 'bg-gray-500/15 text-gray-400', label: 'Diffuse' },
  none: { cls: 'bg-gray-500/15 text-gray-500', label: '—' },
};
function RegimeBadge({ label }) {
  if (!label) return <span className="text-[10px] text-gray-400">—</span>;
  const m = REGIME[label] || REGIME.none;
  return <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${m.cls}`}>{m.label}</span>;
}

// Two data shapes carry the same attribution: the universe roster emits flat
// `${prefix}drift_*` columns (top dates use ret/ret_vendor), while the single-ticker
// report nests a drift_attribution object (top_contributors use ret_a/ret_b). Normalise
// both into one shape so the same TopDatesTable / AttrSummary render either.
const DRIFT_WINDOWS = ['1y', '3y', '5y', '10y'];
// Normalize the trailing-window drift localisation to {w: {cum, share}} from either the scan
// roster (flat drift_w5y_cum/_share cols) or the drill-down report (nested windows{} dict).
const windowsFromRow = (row, prefix) =>
  DRIFT_WINDOWS.reduce((acc, w) => {
    acc[w] = { cum: row[`${prefix}drift_w${w}_cum`], share: row[`${prefix}drift_w${w}_share`] };
    return acc;
  }, {});
const windowsFromReport = (wins) =>
  DRIFT_WINDOWS.reduce((acc, w) => {
    const x = (wins || {})[w] || {};
    acc[w] = { cum: x.cum_drift, share: x.abs_share };
    return acc;
  }, {});

const attrFromRow = (row, prefix) => {
  const label = row[`${prefix}drift_label`];
  if (label === undefined || label === null) return null;
  return {
    label,
    top3: row[`${prefix}drift_top3_share`],
    onDiv: row[`${prefix}drift_on_div_share`],
    windows: windowsFromRow(row, prefix),
    topDates: (row[`${prefix}drift_top_dates`] || []).map((t) => ({
      date: t.date, contribution: t.contribution, ret: t.ret, retVendor: t.ret_vendor,
      onDiv: t.on_dividend, onAction: t.on_action,
    })),
  };
};
const attrFromReport = (a) =>
  !a
    ? null
    : {
        label: a.label,
        top3: a.top3_abs_share,
        onDiv: a.on_dividend_abs_share,
        windows: windowsFromReport(a.windows),
        topDates: (a.top_contributors || []).map((t) => ({
          date: t.date, contribution: t.contribution, ret: t.ret_a, retVendor: t.ret_b,
          onDiv: t.on_dividend, onAction: t.on_action,
        })),
      };

const sharePct = (x) => (x === null || x === undefined ? '—' : `${(x * 100).toFixed(0)}%`);

// How much of the drift accumulated in each trailing window (share of the drift MAGNITUDE).
// The "is this recent?" lens — a name whose drift is ~all in 10y-but-0%-in-5y sits in deep
// back-history no live backtest reaches (every strategy optimises on a ~2016-start window).
function WindowStrip({ windows }) {
  if (!windows) return null;
  const cells = DRIFT_WINDOWS.map((w) => [w, windows[w] && windows[w].share]);
  if (cells.every(([, s]) => s === null || s === undefined)) return null;   // snapshot predates windows
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400 mb-2">
      <span className="uppercase tracking-wide text-[10px]">Drift by window</span>
      {cells.map(([w, s]) => (
        <span key={w}>
          {w.toUpperCase()} <span className="font-mono text-gray-700 dark:text-gray-300">{sharePct(s)}</span>
        </span>
      ))}
    </div>
  );
}

// One-line attribution stat strip: the regime verdict + how localized it is + window localisation.
function AttrSummary({ attr }) {
  if (!attr) return null;
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400 mb-2">
        <span className="flex items-center gap-1.5">Regime <RegimeBadge label={attr.label} /></span>
        <span>
          Top-3 share <span className="font-mono text-gray-700 dark:text-gray-300">{sharePct(attr.top3)}</span>
        </span>
        {attr.onDiv !== null && attr.onDiv !== undefined && (
          <span>
            On dividend <span className="font-mono text-gray-700 dark:text-gray-300">{sharePct(attr.onDiv)}</span>
          </span>
        )}
      </div>
      <WindowStrip windows={attr.windows} />
    </>
  );
}

// The localized contributors — the handful of dates carrying the cumulative drift, each
// tagged Div / Action / Orphan so a reviewer knows which dividend or CA to open first.
function TopDatesTable({ dates }) {
  if (!dates || dates.length === 0) return <Empty>No localized contributors.</Empty>;
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[460px] text-left">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <th className="px-2 py-2 font-normal">Date</th>
            <th className="px-2 py-2 font-normal text-right">Contribution</th>
            <th className="px-2 py-2 font-normal text-right">Our move</th>
            <th className="px-2 py-2 font-normal text-right">Vendor move</th>
            <th className="px-2 py-2 font-normal text-center">Tag</th>
          </tr>
        </thead>
        <tbody>
          {dates.map((t, i) => (
            <tr key={`${t.date}-${i}`} className="border-t border-gray-100 dark:border-gray-800/60">
              <td className="px-2 py-1.5 font-mono text-xs text-gray-700 dark:text-gray-300">{fmtDate(t.date)}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(t.contribution)}`}>{pct(t.contribution)}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(t.ret)}`}>{pct(t.ret)}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(t.retVendor)}`}>{pct(t.retVendor)}</td>
              <td className="px-2 py-1.5 text-center">
                {t.onDiv && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-purple-500/15 text-purple-400">Div</span>
                )}
                {t.onAction && !t.onDiv && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-blue-500/15 text-blue-400">Action</span>
                )}
                {!t.onDiv && !t.onAction && <span className="text-[10px] text-gray-400">Orphan</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// One expandable roster row: the drift verdict on the surface, the localized
// top-contributor dates (the regime + which dividends/CAs carry it) on expand.
function DriftRow({ r, trAvailable }) {
  const [open, setOpen] = useState(false);
  const attr = trAvailable ? r.trAttr || r.splitAttr : r.splitAttr;
  const hasDetail = !!(attr && attr.topDates && attr.topDates.length > 0);
  const cols = trAvailable ? 6 : 4;
  return (
    <>
      <tr
        className={`border-t border-gray-100 dark:border-gray-800/60 ${
          hasDetail ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40' : ''
        }`}
        onClick={hasDetail ? () => setOpen((o) => !o) : undefined}
      >
        <td className="px-2 py-1.5 font-mono text-xs font-medium text-gray-900 dark:text-gray-100">
          <span className="flex items-center gap-1.5">
            {hasDetail ? <Chevron open={open} /> : <span className="w-3.5" />}
            {r.ticker}
          </span>
        </td>
        <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(driftSign(r.split))}`}>{fmtDrift(r.split)}</td>
        {trAvailable && (
          <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(driftSign(r.tr))}`}>{fmtDrift(r.tr)}</td>
        )}
        <td className="px-2 py-1.5 text-center"><RegimeBadge label={attr?.label} /></td>
        <td className="px-2 py-1.5 text-center"><DriftBadge drift={r.split} /></td>
        {trAvailable && <td className="px-2 py-1.5 text-center"><DriftBadge drift={r.tr} /></td>}
      </tr>
      {open && hasDetail && (
        <tr className="bg-gray-50/60 dark:bg-gray-900/30">
          <td colSpan={cols} className="px-3 py-2">
            <AttrSummary attr={attr} />
            <TopDatesTable dates={attr.topDates} />
          </td>
        </tr>
      )}
    </>
  );
}

// Cumulative-drift roster for the universe scan — one row per security, worst first.
// Split-only drift should be ~0 (both sides split-adjusted); a non-zero TR drift is the
// meaningful tell of a dropped/under-counted dividend stream vs the vendor's adjClose.
// Each row expands to the attribution: regime + the dates carrying the drift.
function DriftTable({ securities, trAvailable }) {
  const rows = (securities || [])
    .map((s) => ({
      ticker: s.ticker,
      split: driftFromRow(s, ''),
      tr: trAvailable ? driftFromRow(s, 'tr_') : null,
      splitAttr: attrFromRow(s, ''),
      trAttr: trAvailable ? attrFromRow(s, 'tr_') : null,
    }))
    .filter((r) => driftMag(r.split) >= 0 || driftMag(r.tr) >= 0)
    .sort((a, b) => {
      const am = Math.max(driftMag(trAvailable ? a.tr : a.split), driftMag(a.split));
      const bm = Math.max(driftMag(trAvailable ? b.tr : b.split), driftMag(b.split));
      return bm - am;
    });
  if (rows.length === 0) {
    return <Empty>No drift data — the scan snapshot predates the cumulative-drift axis. Rescan to populate.</Empty>;
  }
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[520px] text-left">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <th className="px-2 py-2 font-normal">Ticker</th>
            <th className="px-2 py-2 font-normal text-right">Split drift</th>
            {trAvailable && <th className="px-2 py-2 font-normal text-right">TR drift</th>}
            <th className="px-2 py-2 font-normal text-center">Regime</th>
            <th className="px-2 py-2 font-normal text-center">Split</th>
            {trAvailable && <th className="px-2 py-2 font-normal text-center">TR</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <DriftRow key={`${r.ticker}-drift-${i}`} r={r} trAvailable={trAvailable} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Single-ticker drift module — split-only + total-return cumulative drift side by side,
// each followed by its attribution (the regime + the dates carrying the drift).
function DriftModule({ split, tr, splitAttr, trAttr, trAvailable }) {
  const worstExceeds = (split && split.exceeds) || (trAvailable && tr && tr.exceeds);
  const yrs = (split && split.years) || (tr && tr.years);
  const sa = attrFromReport(splitAttr);
  const ta = trAvailable ? attrFromReport(trAttr) : null;
  const blocks = [];
  if (sa && sa.topDates.length) blocks.push(['Split-adjusted', sa]);
  if (ta && ta.topDates.length) blocks.push(['Total return', ta]);
  return (
    <Collapsible
      title="Cumulative drift"
      accent="text-gray-900 dark:text-gray-100"
      defaultOpen={!!worstExceeds}
      badge={<DriftBadge drift={worstExceeds ? { basis: 'cumulative_staircase', exceeds: true } : (split || tr)} />}
    >
      <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
        Slow same-sign bias the per-day divergence check is blind to (hundreds of sub-tolerance days compounding).
        Verdict on how far our cumulative window return diverges from the same vendor's own
        {yrs ? ` over ~${yrs.toFixed(1)}yr` : ''} — both relatively and absolutely, never annualized. Split-only
        should be ~0; a non-zero total-return drift points at a dropped/under-counted dividend stream.
      </div>
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto scrollbar-thin">
        <table className="w-full min-w-[420px] text-left">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <th className="px-2 py-2 font-normal">Pair</th>
              <th className="px-2 py-2 font-normal text-right">Drift</th>
              <th className="px-2 py-2 font-normal text-center">Basis</th>
              <th className="px-2 py-2 font-normal text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-gray-100 dark:border-gray-800/60">
              <td className="px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300">Split-adjusted</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(driftSign(split))}`}>{fmtDrift(split)}</td>
              <td className="px-2 py-1.5 text-center text-[10px] uppercase tracking-wide text-gray-400">{split?.basis || '—'}</td>
              <td className="px-2 py-1.5 text-center"><DriftBadge drift={split} /></td>
            </tr>
            {trAvailable && (
              <tr className="border-t border-gray-100 dark:border-gray-800/60">
                <td className="px-2 py-1.5 text-xs text-gray-700 dark:text-gray-300">Total return</td>
                <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(driftSign(tr))}`}>{fmtDrift(tr)}</td>
                <td className="px-2 py-1.5 text-center text-[10px] uppercase tracking-wide text-gray-400">{tr?.basis || '—'}</td>
                <td className="px-2 py-1.5 text-center"><DriftBadge drift={tr} /></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {blocks.length > 0 && (
        <div className="mt-3 space-y-3">
          {blocks.map(([title, a]) => (
            <div key={title}>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                {title} — where the drift came from
              </div>
              <AttrSummary attr={a} />
              <TopDatesTable dates={a.topDates} />
            </div>
          ))}
        </div>
      )}
    </Collapsible>
  );
}

// ── CA REMEDIATION (corrections) ───────────────────────────────────────────
// A separate control surface from the detect-only scan above. "Scan for corrections"
// runs the read-only remediation dry-run (classifies every ticker by outcome). Two
// distinct write recipes, each with its OWN explicit apply button so a dividend is
// never written under the plain price button:
//   • FIXED_BY_SPLIT   — drop a phantom split, rescale the distorted price span ÷F.
//                        Price-only; no dividend touched.
//   • PHANTOM_DIVIDEND_MASK — a phantom whose factor ALSO inflated raw dividends ×F.
//                        Fix = drop phantom + ÷F on BOTH price AND dividend (in-place
//                        cash overwrite behind a note stash). Provably TR-neutral.
// NEEDS_DIVIDEND_FIX and MANUAL_REVIEW are never auto-applied — they need an
// independently-sourced value (auto-writing one would be circular).
const fmtCum = (d) =>
  d && d.cum !== null && d.cum !== undefined ? `${(d.cum * 100).toFixed(2)}%` : '—';

// Summarize the mask_signature OBJECT into a safe one-line string (phantom factor +
// how many dividends it inflated). Like fmtDivReason, this MUST never return the raw
// object — rendering an object as a React child white-screens the panel.
const fmtMaskSig = (sig) => {
  if (!sig || typeof sig !== 'object') return '';
  const parts = [];
  if (sig.factor !== null && sig.factor !== undefined) parts.push(`÷${Number(sig.factor)}`);
  if (sig.n_dividends_inflated) parts.push(`${sig.n_dividends_inflated} div ×F`);
  else if (sig.n_dividends) parts.push(`${sig.n_dividends} div`);
  if (sig.n_phantoms) parts.push(`${sig.n_phantoms} phantom${sig.n_phantoms === 1 ? '' : 's'}`);
  return parts.join(' · ');
};

// Per-row apply status, derived from the last apply report (scope-matched). Lets the
// roster show at a glance which names the Apply button has already written ("Applied"),
// which failed the write ("Failed"), which were blocked ("Skipped"), and which are still
// pending. Purely informational — never gates the Apply button.
function CorrectionStatus({ status }) {
  if (!status || !status.state) {
    return <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">Pending</span>;
  }
  if (status.state === 'applied') {
    return (
      <span
        title={`+${status.rows ?? 0} corrected rows · ${status.flips ?? 0} phantom flip${status.flips === 1 ? '' : 's'}`}
        className="text-[10px] uppercase tracking-wider text-green-500"
      >
        ✓ Applied
      </span>
    );
  }
  if (status.state === 'failed') {
    return (
      <span title={status.error || 'write failed'} className="text-[10px] uppercase tracking-wider text-red-500">
        ✗ Failed
      </span>
    );
  }
  if (status.state === 'skipped') {
    return (
      <span title={status.reason || ''} className="text-[10px] uppercase tracking-wider text-amber-500">
        Skipped
      </span>
    );
  }
  return <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">Pending</span>;
}

// The PHANTOM_DIVIDEND_MASK roster. The key column is the TWO after-drift columns:
// `after_tr` is the price-only fix (drop phantom, rescale price) — for a mask this
// still leaves the dividend axis F× wrong, so its cumulative TR MOVES away from clean;
// `after_tr_divfix` is the full fix (÷F on price AND dividend) which collapses the
// drift back to ~0. That gap is exactly why a mask needs its own dividend-writing
// apply and can't ride the plain price button. Status reflects the last MASK apply.
function MaskTable({ rows, statusByTicker = {} }) {
  if (!rows || rows.length === 0) return <Empty>No dividend-mask corrections in this snapshot.</Empty>;
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[680px] text-left">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <th className="px-2 py-2 font-normal">Ticker</th>
            <th className="px-2 py-2 font-normal">Status</th>
            <th className="px-2 py-2 font-normal text-right">TR before</th>
            <th className="px-2 py-2 font-normal text-right">TR price-only</th>
            <th className="px-2 py-2 font-normal text-right">TR +dividend fix</th>
            <th className="px-2 py-2 font-normal">Mask signature</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.ticker}-mask-${i}`} className="border-t border-gray-100 dark:border-gray-800/60">
              <td className="px-2 py-1.5 font-mono text-xs font-medium text-gray-900 dark:text-gray-100">{r.ticker}</td>
              <td className="px-2 py-1.5"><CorrectionStatus status={statusByTicker[r.ticker]} /></td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(r.before_tr?.cum)}`}>{fmtCum(r.before_tr)}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(r.after_tr?.cum)}`}>{fmtCum(r.after_tr)}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(r.after_tr_divfix?.cum)}`}>{fmtCum(r.after_tr_divfix)}</td>
              <td className="px-2 py-1.5 text-[11px] text-gray-500 dark:text-gray-400">{fmtMaskSig(r.mask_signature)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Summarize the dividend_check OBJECT into a safe string. Rendering the object
// directly as a React child throws "Objects are not valid as a React child" and
// white-screens the whole panel — this is the fix for that crash.
const fmtDivReason = (dc) => {
  if (!dc || typeof dc !== 'object') return '';
  const parts = [];
  if (dc.n_mismatch) parts.push(`${dc.n_mismatch} mismatch`);
  if (dc.n_our_extra) parts.push(`${dc.n_our_extra} extra vs vendor`);
  if (dc.n_vendor_missing) parts.push(`${dc.n_vendor_missing} vendor-only`);
  if (dc.n_matched) parts.push(`${dc.n_matched} matched`);
  return parts.join(', ');
};

// Human-only outcomes (needs-dividend / manual). Shows the before/after TR drift and a
// SAFE stringified reason — never the raw dividend_check object.
// ── Universal correction list (union entry gate) ──────────────────────────
// Each name is flagged by one or more deterministic legs: the FMP staircase on an
// adjusted axis (Split-adj / TR) OR a split-calendar discrepancy vs yfinance
// (Phantom / Missing / Ambiguous). Dividends are NEVER a gate leg. The "Current case"
// column shows which recipe (if any) already claims the row; NEEDS_DIVIDEND_FIX /
// MANUAL_REVIEW = not yet carved into a deterministic case.
const UNION_REASON_META = {
  staircase_split: { label: 'Split-adj', cls: 'text-amber-500 border-amber-500/40 bg-amber-500/10' },
  staircase_tr: { label: 'TR', cls: 'text-amber-400 border-amber-400/40 bg-amber-400/10' },
  phantom: { label: 'Phantom', cls: 'text-blue-400 border-blue-400/40 bg-blue-400/10' },
  missing: { label: 'Missing', cls: 'text-red-500 border-red-500/40 bg-red-500/10' },
  ambiguous: { label: 'Ambiguous', cls: 'text-red-400 border-red-400/40 bg-red-400/10' },
  flagged: { label: 'Flagged', cls: 'text-gray-400 border-gray-400/40 bg-gray-400/10' },
};

const UNION_OUTCOME_META = {
  CLEAN: { label: 'Clean (adj ok)', cls: 'text-gray-400' },
  FIXED_BY_SPLIT: { label: 'Auto-safe (split)', cls: 'text-green-500' },
  PHANTOM_DIVIDEND_MASK: { label: 'Dividend-mask', cls: 'text-purple-400' },
  NEEDS_DIVIDEND_FIX: { label: 'Needs dividend (uncased)', cls: 'text-amber-400' },
  MANUAL_REVIEW: { label: 'Manual (uncased)', cls: 'text-amber-500' },
};

function ReasonChips({ reasons }) {
  if (!reasons || reasons.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {reasons.map((rn) => {
        const meta = UNION_REASON_META[rn] || { label: rn, cls: 'text-gray-400 border-gray-400/40 bg-gray-400/10' };
        return (
          <span key={rn} className={`px-1.5 py-0.5 rounded border text-[10px] ${meta.cls}`}>
            {meta.label}
          </span>
        );
      })}
    </div>
  );
}

// Derive the union list from an OLD snapshot that predates the backend `union` field:
// concatenate the four non-clean buckets and annotate reasons from what's available
// (before_tr/before_split staircase verdicts + the phantom_tickers calendar map). A
// best-effort view so the page shows the universal list without waiting for a rescan;
// once a fresh scan writes `rem.union`, that precise list supersedes this.
function buildUnionFallback(rem) {
  if (!rem) return [];
  const calMap = {};
  for (const p of rem.phantom_tickers || []) {
    calMap[p.ticker] = {
      phantom: (p.phantom || []).length,
      missing: (p.missing || []).length,
      ambiguous: (p.ambiguous || []).length,
    };
  }
  const buckets = [
    ...(rem.fixed_by_split || []),
    ...(rem.phantom_dividend_mask || []),
    ...(rem.fmp_tr_corrupt || []),
    ...(rem.needs_dividend_fix || []),
    ...(rem.manual_review || []),
  ];
  const seen = new Set();
  const out = [];
  for (const r of buckets) {
    if (!r?.ticker || seen.has(r.ticker)) continue;
    seen.add(r.ticker);
    const cm = calMap[r.ticker] || {};
    const reasons = [];
    if (r.before_split?.exceeds) reasons.push('staircase_split');
    if (r.before_tr?.exceeds) reasons.push('staircase_tr');
    if (cm.phantom) reasons.push('phantom');
    if (cm.missing) reasons.push('missing');
    if (cm.ambiguous) reasons.push('ambiguous');
    out.push({
      ticker: r.ticker,
      outcome: r.outcome,
      reasons: reasons.length ? reasons : ['flagged'],
      before_split: r.before_split,
      before_tr: r.before_tr,
      n_phantom: cm.phantom || 0,
      n_missing: cm.missing || 0,
      n_ambiguous: cm.ambiguous || 0,
    });
  }
  return out;
}

// Sort spec for the universal-residue table. Numeric columns sort by drift
// MAGNITUDE (|cum|) so "most drift" is unambiguous regardless of sign; nulls
// always sink to the bottom no matter the direction. String columns are plain
// locale compares. `null` sort state = preserve backend order (worst-TR first).
const UNION_SORT_COLS = {
  ticker:       { type: 'str', get: (r) => r.ticker || '' },
  reasons:      { type: 'str', get: (r) => (r.reasons || []).join(',') },
  before_split: { type: 'mag', get: (r) => r.before_split?.cum },
  before_tr:    { type: 'mag', get: (r) => r.before_tr?.cum },
  before_tr_10y:{ type: 'mag', get: (r) => r.before_tr_10y?.cum },
  outcome:      { type: 'str', get: (r) => (UNION_OUTCOME_META[r.outcome]?.label || r.outcome || '') },
};

function UnionTable({ rows, onPick }) {
  // null = keep backend order; else { key, dir: 'asc'|'desc' }.
  const [sort, setSort] = useState(null);

  const sorted = useMemo(() => {
    if (!rows) return [];
    if (!sort) return rows;
    const col = UNION_SORT_COLS[sort.key];
    if (!col) return rows;
    const factor = sort.dir === 'asc' ? 1 : -1;
    // Stable sort over a shallow copy so equal keys keep backend order.
    return rows
      .map((r, i) => [r, i])
      .sort(([a, ai], [b, bi]) => {
        let cmp;
        if (col.type === 'mag') {
          const av = col.get(a);
          const bv = col.get(b);
          const an = av === null || av === undefined || Number.isNaN(av);
          const bn = bv === null || bv === undefined || Number.isNaN(bv);
          // Nulls always sink, independent of direction.
          if (an && bn) cmp = 0;
          else if (an) return 1;
          else if (bn) return -1;
          else cmp = (Math.abs(av) - Math.abs(bv)) * factor;
        } else {
          cmp = String(col.get(a)).localeCompare(String(col.get(b))) * factor;
        }
        return cmp !== 0 ? cmp : ai - bi;
      })
      .map(([r]) => r);
  }, [rows, sort]);

  if (!rows || rows.length === 0) {
    return <Empty>Nothing flagged — every name is clean on both gates (adjusted matches FMP and the split calendar matches yfinance).</Empty>;
  }

  const onSort = (key) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) {
        // First click: numeric cols default to descending (most drift first);
        // string cols default to ascending (A→Z).
        const dir = UNION_SORT_COLS[key]?.type === 'mag' ? 'desc' : 'asc';
        return { key, dir };
      }
      if (prev.dir === (UNION_SORT_COLS[key]?.type === 'mag' ? 'desc' : 'asc')) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      // Third click on the same column clears the sort → backend order.
      return null;
    });
  };

  const arrow = (key) => {
    if (!sort || sort.key !== key) return '';
    return sort.dir === 'asc' ? ' ▲' : ' ▼';
  };

  const Th = ({ colKey, children, align = 'left' }) => (
    <th
      className={`px-2 py-2 font-normal cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 ${align === 'right' ? 'text-right' : ''} ${sort?.key === colKey ? 'text-brand-gold' : ''}`}
      onClick={() => onSort(colKey)}
      title="Click to sort; click again to reverse, a third time to reset"
    >
      {children}{arrow(colKey)}
    </th>
  );

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <Th colKey="ticker">Ticker</Th>
            <Th colKey="reasons">Flagged by</Th>
            <Th colKey="before_split" align="right">Split-adj drift</Th>
            <Th colKey="before_tr" align="right">TR drift</Th>
            <Th colKey="before_tr_10y" align="right">TR drift 10Y</Th>
            <Th colKey="outcome">Current case</Th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r, i) => {
            const om = UNION_OUTCOME_META[r.outcome] || { label: r.outcome || '—', cls: 'text-gray-400' };
            return (
              <tr key={`${r.ticker}-u-${i}`} className="border-t border-gray-100 dark:border-gray-800/60 first:border-t-0">
                <td className="px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => onPick && onPick(r.ticker)}
                    className="font-mono text-xs font-medium text-brand-gold underline decoration-dotted underline-offset-2 hover:text-brand-gold/80"
                    title="Day-by-day TR comparison"
                  >
                    {r.ticker}
                  </button>
                </td>
                <td className="px-2 py-1.5"><ReasonChips reasons={r.reasons} /></td>
                <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(r.before_split?.cum)}`}>{fmtCum(r.before_split)}</td>
                <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(r.before_tr?.cum)}`}>{fmtCum(r.before_tr)}</td>
                <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(r.before_tr_10y?.cum)}`}>{fmtCum(r.before_tr_10y)}</td>
                <td className={`px-2 py-1.5 text-[11px] font-medium ${om.cls}`}>{om.label}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Carve-out 2 — fmp_tr_corrupt (TPL class). Ours-authoritative / FMP-corrupt: the row exists
// only because our total-return series diverges from FMP, but everything that could make US
// wrong is clean AND the independent third source (yfinance TR) corroborates us within a hard
// 10-percentage-point cap. Split calendar clean, split-adjusted matches FMP, no dividend
// AMOUNT mismatch, |ours − yfinance| < 10pp ⇒ FMP is the outlier. NO ACTION NEEDED — this
// table documents WHY the name is safe to leave alone (it is not a fix, it is an exoneration).
function FmpTrCorruptTable({ rows, onPick }) {
  if (!rows || rows.length === 0) {
    return <Empty>None — no name where yfinance corroborates our series against a corrupt FMP total return.</Empty>;
  }
  const yfCell = (r) => {
    const d = r.yf_tr?.cum_drift;
    if (d === null || d === undefined) return { txt: 'n/a', cls: 'text-gray-400' };
    const pp = (d * 100).toFixed(2);
    // green = tight agreement with the third source (below cap); the cap is 10pp.
    return { txt: `${pp}pp`, cls: Math.abs(d) < 0.10 ? 'text-emerald-500' : 'text-red-500' };
  };
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <th className="px-2 py-2 font-normal">Ticker</th>
            <th className="px-2 py-2 font-normal text-right">TR drift vs FMP</th>
            <th className="px-2 py-2 font-normal text-right">Ours vs yfinance</th>
            <th className="px-2 py-2 font-normal text-right">Dividends</th>
            <th className="px-2 py-2 font-normal">Verdict</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const yf = yfCell(r);
            const dc = r.dividend_check || {};
            return (
              <tr key={`${r.ticker}-c2-${i}`} className="border-t border-gray-100 dark:border-gray-800/60 first:border-t-0">
                <td className="px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => onPick && onPick(r.ticker)}
                    className="font-mono text-xs font-medium text-brand-gold underline decoration-dotted underline-offset-2 hover:text-brand-gold/80"
                    title="Day-by-day TR comparison"
                  >
                    {r.ticker}
                  </button>
                </td>
                <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(r.before_tr?.cum)}`}>{fmtCum(r.before_tr)}</td>
                <td className={`px-2 py-1.5 font-mono text-xs text-right ${yf.cls}`}>{yf.txt}</td>
                <td className="px-2 py-1.5 font-mono text-[11px] text-right text-gray-500 dark:text-gray-400">
                  {dc.n_matched ?? 0} matched · {dc.n_mismatch ?? 0} diff
                </td>
                <td className="px-2 py-1.5 text-[11px] font-medium text-emerald-500">Ours authoritative — no action</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Day-by-day total-return drill-down popup. Opened by clicking any ticker in the
// Universal residue (or split-TR-fix) table — answers "where is the cumulative drift
// coming from?" by listing every shared consecutive-day pair's daily-return delta
// (ours - vendor), sorted largest-magnitude-first. A parallel SPLIT-only column lets the
// reviewer tell a split-driven day (shows in both) from a dividend/TR-only one (TR-only).
// READ-ONLY: fetches the backend comparison, writes nothing.
function DailyTrModal({ ticker, onClose }) {
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('tr'); // 'tr' | 'split'

  useEffect(() => {
    if (!ticker) return undefined;
    let live = true;
    setLoading(true);
    setErr(null);
    setRep(null);
    setTab('tr');
    tradingService
      .getDailyTrComparison(ticker, 400)
      .then(({ data }) => { if (live) setRep(data); })
      .catch(() => { if (live) setErr('Could not load the daily comparison. Check server logs.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [ticker]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  if (!ticker) return null;

  const noData = rep && rep.outcome === 'NO_DATA';
  const rows = rep ? (tab === 'tr' ? rep.tr_deltas : rep.split_deltas) || [] : [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-3 sm:p-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
          <div>
            <h4 className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">{ticker}</h4>
            <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              Day-by-day total-return diff — ours vs FMP, sorted by |delta|
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            Close ✕
          </button>
        </div>

        <div className="px-4 py-3">
          {loading && <p className="py-6 text-center text-xs text-gray-500 dark:text-gray-400">Fetching vendor series…</p>}
          {err && <p className="py-6 text-center text-xs text-red-500">{err}</p>}
          {noData && <Empty>No FMP price rows on the spine for {ticker}.</Empty>}

          {rep && !noData && (
            <>
              <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                <StatTile label="Window from" value={fmtDate(rep.window_start)} />
                <StatTile label="Days compared" value={rep.n_compared} />
                <StatTile label="Nonzero deltas" value={rep.n_nontrivial} />
                <StatTile
                  label="Cum drift vs FMP"
                  value={pct(rep.cum_drift)}
                  accent={rep.exceeds ? (rep.cum_drift >= 0 ? 'text-emerald-500' : 'text-red-500') : undefined}
                />
                <StatTile
                  label="Cum drift vs yf"
                  value={rep.yf_tr_available ? pct(rep.cum_drift_yf) : '—'}
                  accent={
                    !rep.yf_tr_available
                      ? undefined
                      : rep.exceeds_yf
                      ? (rep.cum_drift_yf >= 0 ? 'text-emerald-500' : 'text-red-500')
                      : 'text-gray-400'
                  }
                />
              </div>
              <p className="mb-3 text-[10px] leading-snug text-gray-400">
                Two independent references. If ours ≈ <span className="font-medium">yf</span> but
                diverges from <span className="font-medium">FMP</span>, FMP's TR is the outlier;
                if ours diverges from <span className="font-medium">both</span>, ours is suspect.
                A near-zero split-only tab does <span className="font-medium">not</span> prove
                splits are clean — a shared wrong CA can hide in it.
              </p>

              <div className="mb-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTab('tr')}
                  className={`rounded-md px-2.5 py-1 text-[11px] font-medium ${tab === 'tr' ? 'bg-brand-gold/20 text-brand-gold' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                >
                  Total return ({(rep.tr_deltas || []).length})
                </button>
                <button
                  type="button"
                  onClick={() => setTab('split')}
                  className={`rounded-md px-2.5 py-1 text-[11px] font-medium ${tab === 'split' ? 'bg-brand-gold/20 text-brand-gold' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                >
                  Split-only ({(rep.split_deltas || []).length})
                </button>
                <span className="ml-auto text-[10px] text-gray-400">
                  {tab === 'tr' ? 'Div + split adjusted' : 'Split adjusted only (isolates dividend gaps)'}
                </span>
              </div>

              {rows.length === 0 ? (
                <Empty>No daily delta above 1e-6 on this axis — the two series agree day-by-day.</Empty>
              ) : (
                <div className="max-h-[52vh] overflow-y-auto overflow-x-auto rounded-lg border border-gray-200 scrollbar-thin dark:border-gray-800">
                  <table className="w-full min-w-[720px] text-left">
                    <thead className="sticky top-0 bg-white dark:bg-gray-900">
                      <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <th className="px-2 py-2 font-normal">Date</th>
                        <th className="px-2 py-2 font-normal">Prev</th>
                        <th className="px-2 py-2 font-normal text-right">Ours</th>
                        <th className="px-2 py-2 font-normal text-right">Vendor</th>
                        <th className="px-2 py-2 font-normal text-right">Delta</th>
                        <th className="px-2 py-2 font-normal text-right">Cum → date</th>
                        <th className="px-2 py-2 font-normal">Corp action (ours vs yf)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const ca = fmtCa(r.ca);
                        return (
                          <tr key={`${r.date}-${i}`} className="border-t border-gray-100 dark:border-gray-800/60 first:border-t-0">
                            <td className="px-2 py-1.5 font-mono text-[11px] text-gray-900 dark:text-gray-100">{fmtDate(r.date)}</td>
                            <td className="px-2 py-1.5 font-mono text-[11px] text-gray-500 dark:text-gray-400">{fmtDate(r.prev_date)}</td>
                            <td className={`px-2 py-1.5 font-mono text-[11px] text-right ${moveColor(r.ret_ours)}`}>{pct(r.ret_ours)}</td>
                            <td className={`px-2 py-1.5 font-mono text-[11px] text-right ${moveColor(r.ret_vendor)}`}>{pct(r.ret_vendor)}</td>
                            <td className={`px-2 py-1.5 font-mono text-[11px] text-right font-semibold ${moveColor(r.delta)}`}>{pct(r.delta)}</td>
                            <td className={`px-2 py-1.5 font-mono text-[11px] text-right ${moveColor(r.cum_to_date)}`}>{pct(r.cum_to_date)}</td>
                            <td className={`px-2 py-1.5 text-[11px] ${ca.cls}`}>{ca.text}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function OutcomeList({ rows, showDividend = false, reasonKey }) {
  if (!rows || rows.length === 0) return <Empty>None.</Empty>;
  // Per-row reason cell. NEVER render a raw value that might be an object — the
  // dividend_check field is an object and rendering it directly white-screens the
  // panel ("Objects are not valid as a React child"). Objects go through fmtDivReason.
  const reasonOf = (r) => {
    if (showDividend) return fmtDivReason(r.dividend_check) || 'dividend axis — needs independent source';
    if (reasonKey) {
      const v = r[reasonKey];
      if (v === null || v === undefined) return '';
      return typeof v === 'object' ? fmtDivReason(v) : String(v);
    }
    return 'cause undetermined — inspect corporate-action history';
  };
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[420px] text-left">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <th className="px-2 py-2 font-normal">Ticker</th>
            <th className="px-2 py-2 font-normal text-right">TR drift before</th>
            <th className="px-2 py-2 font-normal text-right">TR drift after</th>
            <th className="px-2 py-2 font-normal">{showDividend ? 'Dividend check' : 'Reason'}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.ticker}-oc-${i}`} className="border-t border-gray-100 dark:border-gray-800/60 first:border-t-0">
              <td className="px-2 py-1.5 font-mono text-xs font-medium text-gray-900 dark:text-gray-100">{r.ticker}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(r.before_tr?.cum)}`}>{fmtCum(r.before_tr)}</td>
              <td className={`px-2 py-1.5 font-mono text-xs text-right ${moveColor(r.after_tr?.cum)}`}>{fmtCum(r.after_tr)}</td>
              <td className="px-2 py-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                {reasonOf(r)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApplyReport({ report }) {
  if (!report) return null;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <StatTile label="Applied" value={report.n_applied ?? 0} accent="text-green-500" />
        <StatTile label="Skipped" value={report.n_skipped ?? 0} accent={(report.n_skipped ?? 0) > 0 ? 'text-amber-500' : undefined} />
        <StatTile label="Failed" value={report.n_failed ?? 0} accent={(report.n_failed ?? 0) > 0 ? 'text-red-500' : undefined} />
        <StatTile label="Rows written" value={report.total_corrected_rows_written ?? 0} />
        <StatTile label="Phantoms flipped" value={report.total_phantoms_flipped ?? 0} />
        <StatTile
          label="Dividends corrected"
          value={report.total_dividends_corrected ?? 0}
          accent={(report.total_dividends_corrected ?? 0) > 0 ? 'text-purple-400' : undefined}
        />
      </div>
      <div className="text-[10px] text-gray-500 dark:text-gray-400">Last apply {fmtTs(report.generated_at)}</div>
      {(report.applied?.length ?? 0) > 0 && (
        <Collapsible title="Applied" count={report.applied.length} accent="text-green-500">
          <div className="font-mono text-[11px] text-gray-600 dark:text-gray-300 break-words">
            {report.applied.map((a) => `${a.ticker} (+${a.corrected_rows_written} rows, ${a.phantoms_flipped} flip${a.phantoms_flipped === 1 ? '' : 's'})`).join(', ')}
          </div>
        </Collapsible>
      )}
      {(report.skipped?.length ?? 0) > 0 && (
        <Collapsible title="Skipped" count={report.skipped.length} accent="text-amber-500">
          <OutcomeList rows={report.skipped} reasonKey="block_reason" />
        </Collapsible>
      )}
      {(report.failed?.length ?? 0) > 0 && (
        <Collapsible title="Failed" count={report.failed.length} accent="text-red-500">
          <OutcomeList rows={report.failed} reasonKey="error" />
        </Collapsible>
      )}
    </div>
  );
}

// Scope of a universe scan / corrections run. 'active' = names referenced by an
// active strategy (~478, fast). 'all' = every security with a raw ledger (~8.5k —
// the full backtestable universe; a long background sweep). 'sp_500' = the current
// S&P 500 members (~503 — the relative-momentum factor-feed certification slice).
// Each scope persists its OWN snapshot, so switching the toggle just loads that
// scope's last result.
const SCAN_SCOPES = [
  { key: 'active', label: 'Active strategies', hint: '~478 active-strategy names — fast',
    queuedMsg: 'Correction scan queued — read-only, runs in the background (a few minutes). Hit Refresh to load the classification when it lands.',
    rescanMsg: 'Rescan queued — it runs in the background (a few minutes). Hit Refresh to load the new snapshot when it lands.',
    subtitle: 'All flags across active-strategy names' },
  { key: 'sp_500', label: 'S&P 500', hint: '~503 current members — factor-feed certification slice',
    queuedMsg: 'S&P 500 correction scan queued — a background sweep over the ~503 current members (~15–20 min). It processes in batches and self-resumes; hit Refresh to load the classification when it lands.',
    rescanMsg: 'S&P 500 rescan queued — a background sweep over the ~503 current members (~15–20 min). It self-resumes; hit Refresh to load the new snapshot when it lands.',
    subtitle: 'All flags across current S&P 500 members' },
  { key: 'all', label: 'All priced', hint: '~8.5k priced securities — long background sweep (~1–2h)',
    queuedMsg: 'All-universe correction scan queued — a long background sweep (~1–2h, ~8.5k names). It processes in batches and self-resumes; hit Refresh to watch progress and load the classification when it lands.',
    rescanMsg: 'All-universe rescan queued — a long background sweep (~1–2h, ~8.5k priced names). It processes in batches and self-resumes; hit Refresh to watch progress and load the snapshot when it lands.',
    subtitle: 'All flags across every priced security' },
];
const scopeMeta = (key) => SCAN_SCOPES.find((s) => s.key === key) || SCAN_SCOPES[0];

function ScopeToggle({ scope, onChange, disabled }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden" role="group" aria-label="Scan scope">
      {SCAN_SCOPES.map((o) => (
        <button
          key={o.key}
          type="button"
          disabled={disabled}
          onClick={() => onChange(o.key)}
          title={o.hint}
          aria-pressed={scope === o.key}
          className={`px-2.5 py-1.5 text-[11px] font-medium transition-colors disabled:opacity-40 ${
            scope === o.key
              ? 'bg-brand-gold/20 text-brand-gold'
              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// In-flight progress for a chunked scan. Shows nothing when there is no checkpoint
// or the run is already complete; a compact bar + count while a sweep is running.
function ScanProgress({ progress }) {
  if (!progress || progress.complete) return null;
  const done = Number(progress.processed || 0);
  const total = Number(progress.total || 0);
  const pct = total > 0 ? Math.min(100, Math.round((100 * done) / total)) : 0;
  return (
    <div className="rounded-lg border border-brand-gold/30 bg-brand-gold/5 px-3 py-2 space-y-1">
      <div className="flex items-center justify-between text-[11px] text-brand-gold">
        <span>Scan in progress — running in the background</span>
        <span>{done.toLocaleString()} / {total.toLocaleString()} ({pct}%)</span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
        <div className="h-full bg-brand-gold/70 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-gray-500 dark:text-gray-400">
        Hit Refresh to update. The snapshot below updates only when the sweep finishes.
      </div>
    </div>
  );
}

function CorrectionsSection({ isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmingMask, setConfirmingMask] = useState(false);
  const [scope, setScope] = useState('active');
  const [drillTicker, setDrillTicker] = useState(null); // day-by-day TR popup

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await tradingService.getPriceCorrection(scope);
      setData(d);
    } catch (err) {
      setMsg('Could not load corrections. Check server logs.');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  const doScan = async () => {
    setMsg(null);
    try {
      await tradingService.triggerCorrectionScan(scope);
      setMsg(scopeMeta(scope).queuedMsg);
    } catch (err) {
      const s = err?.response?.status;
      setMsg(s === 403 ? 'Only an admin can run corrections.' : s === 429 ? 'Rate limit hit — try again later.' : 'Could not queue the scan.');
    }
  };

  const doApply = async () => {
    setConfirming(false);
    setMsg(null);
    try {
      await tradingService.triggerCorrectionApply(scope);
      setMsg('Apply queued — writing the auto-safe corrections in the background. Each write is reversible. Hit Refresh to load the apply report when it lands.');
    } catch (err) {
      const s = err?.response?.status;
      setMsg(s === 403 ? 'Only an admin can run corrections.' : s === 429 ? 'Rate limit hit — try again later.' : 'Could not queue the apply.');
    }
  };

  const doApplyMask = async () => {
    setConfirmingMask(false);
    setMsg(null);
    try {
      await tradingService.triggerCorrectionApplyMask(scope);
      setMsg('Dividend-mask apply queued — dropping the phantom and dividing both price AND dividend by F in the background. Vendor cash is overwritten in place with the original stashed behind the note; every write is reversible. Hit Refresh to load the apply report when it lands.');
    } catch (err) {
      const s = err?.response?.status;
      setMsg(s === 403 ? 'Only an admin can run corrections.' : s === 429 ? 'Rate limit hit — try again later.' : 'Could not queue the mask apply.');
    }
  };

  const rem = data?.remediation || null;
  const never = !rem;
  const mask = rem?.phantom_dividend_mask || [];
  const errs = rem?.errors || [];
  const nMask = mask.length;

  // Universal correction list (union entry gate). Prefer the backend-computed `union`
  // (precise — includes CLEAN-but-adjusted-diverged edge names); fall back to a list
  // derived client-side from the buckets so an older snapshot still shows something.
  const union = rem?.union ? rem.union : buildUnionFallback(rem);
  const unionByReason = rem?.union_counts?.by_reason || null;

  // Two carve-outs pulled OUT of the union (user's taxonomy). Carve-out 1 = dividend_mask
  // (the phantom whose factor also inflated dividends — its own PHANTOM_DIVIDEND_MASK
  // section). Carve-out 2 = fmp_tr_corrupt (TPL class: ours-authoritative, yfinance
  // corroborates us within 10pp, FMP is the outlier — no action needed). "Universal" is
  // everything NOT in a carve-out — the residue, sorted by drift (backend already sorts).
  // An OLD snapshot has no `category` field on its rows → those fall through to the
  // residue (category undefined), preserving the pre-carve-out behaviour until a rescan.
  const fmpTrCorrupt = union.filter((r) => r.category === 'fmp_tr_corrupt');
  const universalResidue = union.filter((r) => !r.category);
  const nFmpTrCorrupt = fmpTrCorrupt.length;
  const nUniversal = universalResidue.length;
  // FIXED_BY_SPLIT is folded into the universal residue (no separate bucket) but stays
  // auto-applicable — its count drives the split Apply button. Derived from the union so
  // it survives the payload no longer carrying a dedicated fixed_by_split roster.
  const nFixed = union.filter((r) => r.outcome === 'FIXED_BY_SPLIT').length;

  // Dividend-mask apply report (separate S3 key / separate button).
  const applyReportMask = data?.apply_report_mask || null;
  const maskReportForScope = applyReportMask && (!applyReportMask.scope || applyReportMask.scope === scope) ? applyReportMask : null;
  const maskStatusByTicker = {};
  if (maskReportForScope) {
    for (const a of maskReportForScope.applied || []) {
      maskStatusByTicker[a.ticker] = { state: 'applied', rows: a.corrected_rows_written, flips: a.phantoms_flipped };
    }
    for (const s of maskReportForScope.skipped || []) {
      maskStatusByTicker[s.ticker] = { state: 'skipped', reason: s.block_reason };
    }
    for (const f of maskReportForScope.failed || []) {
      maskStatusByTicker[f.ticker] = { state: 'failed', error: f.error };
    }
  }

  return (
    <section className="space-y-3 border-t border-gray-200 dark:border-gray-800 pt-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Corrections</h3>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">
            {rem ? `Last classified ${fmtTs(rem.generated_at)}` : 'Deterministic phantom remediation (split + dividend-mask) — never invents a dividend value'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ScopeToggle scope={scope} onChange={setScope} disabled={loading} />
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors disabled:opacity-40"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          {isAdmin && (
            <button
              onClick={doScan}
              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
            >
              Scan for corrections
            </button>
          )}
          {isAdmin && nFixed > 0 && (
            <button
              onClick={() => setConfirming(true)}
              className="px-3 py-1.5 rounded-lg bg-brand-gold/15 border border-brand-gold/40 text-brand-gold text-xs font-medium hover:bg-brand-gold/25 transition-colors"
            >
              Apply {nFixed} safe correction{nFixed === 1 ? '' : 's'}
            </button>
          )}
          {isAdmin && nMask > 0 && (
            <button
              onClick={() => setConfirmingMask(true)}
              className="px-3 py-1.5 rounded-lg bg-purple-500/15 border border-purple-500/40 text-purple-400 text-xs font-medium hover:bg-purple-500/25 transition-colors"
            >
              Apply {nMask} dividend-mask fix{nMask === 1 ? '' : 'es'}
            </button>
          )}
        </div>
      </div>

      {confirming && (
        <div className="rounded-lg border border-brand-gold/40 bg-brand-gold/10 px-3 py-2.5 space-y-2">
          <div className="text-xs text-gray-700 dark:text-gray-200">
            This writes corrected price rows + flips phantom splits for <span className="font-medium text-brand-gold">{nFixed}</span>{' '}
            FIXED_BY_SPLIT name{nFixed === 1 ? '' : 's'}. Vendor rows are never touched; every write is reversible. Proceed?
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={doApply}
              className="px-3 py-1.5 rounded-lg bg-brand-gold/20 border border-brand-gold/50 text-brand-gold text-xs font-medium hover:bg-brand-gold/30 transition-colors"
            >
              Confirm write
            </button>
            <button
              onClick={() => setConfirming(false)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirmingMask && (
        <div className="rounded-lg border border-purple-500/40 bg-purple-500/10 px-3 py-2.5 space-y-2">
          <div className="text-xs text-gray-700 dark:text-gray-200">
            This writes corrected price rows, flips phantom splits, AND overwrites raw
            dividend cash in place (÷F) for <span className="font-medium text-purple-400">{nMask}</span>{' '}
            PHANTOM_DIVIDEND_MASK name{nMask === 1 ? '' : 's'}. The original cash amount is stashed behind the
            dividend note, so every write — price and dividend — is reversible. The ÷F on both axes is provably
            total-return-neutral (it collapses the drift). Proceed?
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={doApplyMask}
              className="px-3 py-1.5 rounded-lg bg-purple-500/20 border border-purple-500/50 text-purple-400 text-xs font-medium hover:bg-purple-500/30 transition-colors"
            >
              Confirm dividend-mask write
            </button>
            <button
              onClick={() => setConfirmingMask(false)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {msg && (
        <div className="rounded-lg border border-brand-gold/30 bg-brand-gold/10 px-3 py-2 text-xs text-brand-gold">{msg}</div>
      )}

      <ScanProgress progress={data?.progress} />

      {never && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-6 text-center text-xs text-gray-500 dark:text-gray-400">
          No correction scan yet.{' '}
          {isAdmin ? 'Hit “Scan for corrections” to classify the universe.' : 'Ask an admin to run a correction scan.'}
        </div>
      )}

      {rem && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <StatTile label="Classified" value={rem.n_total ?? 0} />
            <StatTile label="Dividend-mask" value={nMask} accent={nMask > 0 ? 'text-purple-400' : undefined} />
            <StatTile label="FMP-corrupt (ours OK)" value={nFmpTrCorrupt} accent={nFmpTrCorrupt > 0 ? 'text-sky-400' : undefined} />
            <StatTile label="Universal residue" value={nUniversal} accent={nUniversal > 0 ? 'text-brand-gold' : undefined} />
            <StatTile label="Errors / no-data" value={errs.length} accent={errs.length > 0 ? 'text-red-500' : undefined} />
          </div>

          <Collapsible
            title="Universal (uncased residue)"
            count={nUniversal}
            accent="text-brand-gold"
            defaultOpen
          >
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
              Names flagged by a deterministic gate — the FMP staircase on an
              adjusted axis (<span className="text-amber-500">Split-adj</span> / <span className="text-amber-400">TR</span>,
              catches anything that corrupts the series a backtest reads) OR a split-calendar
              discrepancy vs yfinance (<span className="text-blue-400">Phantom</span> / <span className="text-red-500">Missing</span> /
              <span className="text-red-400"> Ambiguous</span>) — that NO fixable case has claimed yet.
              Dividend dollar amounts are never a gate. As we carve cases out of the union one at a time
              (<span className="text-green-500">Auto-safe (split)</span>, <span className="text-purple-400">Dividend-mask</span>,{' '}
              <span className="text-sky-400">FMP-corrupt (ours OK)</span> so far), this residue shrinks — sorted by
              cumulative drift, worst first.
            </div>
            {unionByReason && (
              <div className="flex flex-wrap gap-1 mb-2">
                {Object.entries(unionByReason).map(([rn, n]) => {
                  const meta = UNION_REASON_META[rn] || { label: rn, cls: 'text-gray-400 border-gray-400/40 bg-gray-400/10' };
                  return (
                    <span key={rn} className={`px-1.5 py-0.5 rounded border text-[10px] ${meta.cls}`}>
                      {meta.label} · {n}
                    </span>
                  );
                })}
              </div>
            )}
            <UnionTable rows={universalResidue} onPick={setDrillTicker} />
          </Collapsible>

          <Collapsible
            title="Dividend-mask (PHANTOM_DIVIDEND_MASK)"
            count={nMask}
            accent="text-purple-400"
            defaultOpen={nMask > 0}
          >
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
              A phantom split whose factor ALSO inflated every raw dividend ×F. The cumulative total return reads
              correct as-is (self-masking), but the raw price AND dividend axes are both F× wrong — a live-money
              landmine. The fix drops the phantom and divides BOTH axes by F (dividend cash overwritten in place,
              original stashed behind the note). The “+dividend fix” column collapses the drift back to ~0; the
              “price-only” column is what a plain split fix would leave (still wrong). Fully reversible.
            </div>
            {maskReportForScope && (
              <div className="text-[11px] mb-2 text-gray-500 dark:text-gray-400">
                <span className="text-purple-400 font-medium">{maskReportForScope.applied?.length || 0} applied</span>
                {' · '}
                <span className="text-purple-400">{maskReportForScope.total_dividends_corrected || 0} dividends corrected</span>
                {' · last apply '}{fmtTs(maskReportForScope.generated_at)}
              </div>
            )}
            <MaskTable rows={mask} statusByTicker={maskStatusByTicker} />
          </Collapsible>

          <Collapsible
            title="FMP-corrupt / ours-correct (TPL class)"
            count={nFmpTrCorrupt}
            accent="text-sky-400"
            defaultOpen={nFmpTrCorrupt > 0}
          >
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
              The row exists ONLY because our total return diverges from FMP — but everything that
              could make US wrong is clean (split calendar clean, split-adjusted matches FMP, no
              dividend AMOUNT mismatch) AND the independent third source (yfinance TR) corroborates
              us within a hard <span className="text-sky-400">10-percentage-point</span> cap. Ours ≈ yfinance
              but ours ≠ FMP ⇒ FMP is the outlier, not us. There is NOTHING to fix on our side — this
              section documents WHY the name is safe to leave alone (an exoneration, not a fix). No
              write, no apply.
            </div>
            <FmpTrCorruptTable rows={fmpTrCorrupt} onPick={setDrillTicker} />
          </Collapsible>

          {data?.apply_report && (
            <Collapsible
              title="Last apply report (split)"
              accent="text-gray-900 dark:text-gray-100"
              defaultOpen={false}
            >
              <ApplyReport report={data.apply_report} />
            </Collapsible>
          )}

          {data?.apply_report_mask && (
            <Collapsible
              title="Last apply report (dividend-mask)"
              accent="text-purple-400"
              defaultOpen={false}
            >
              <ApplyReport report={data.apply_report_mask} />
            </Collapsible>
          )}
        </div>
      )}

      {drillTicker && <DailyTrModal ticker={drillTicker} onClose={() => setDrillTicker(null)} />}
    </section>
  );
}

const Legend = () => (
  <div className="text-[11px] text-gray-500 dark:text-gray-400 space-y-1 leading-relaxed">
    <p>
      <span className="text-gray-700 dark:text-gray-300">Split-adjusted</span> module compares our split-only series
      with the vendor's split-adjusted close (dividends excluded both sides — catches a wrong/missing split or
      spinoff factor). <span className="text-gray-700 dark:text-gray-300">Total return</span> module compares our
      split+dividend series with the vendor's adjClose (the dividend cross-check — catches a missing/wrong dividend,
      an adjDividend leak, or a special distribution we never sourced).
    </p>
    <p>
      <span className="text-amber-500">Divergence</span> — our return differs from the vendor's on the
      same day (catches an our-only or vendor-only adjustment bug). <span className="text-purple-400">Impossible gap</span> —
      a ≥50% single-day move on our series alone (catches errors both vendors share, e.g. a spinoff
      mishandled as a split).
    </p>
    <p>
      <span className="text-blue-400">On action</span> — the flag lands on a split/spinoff ex-date, so the
      action is the prime suspect. <span className="text-gray-400">Orphan</span> — no action nearby; the bar
      itself is suspect (or it's a genuine extreme real move).
    </p>
    <p>
      <span className="text-gray-700 dark:text-gray-300">Cumulative drift</span> — the third axis, orthogonal to the
      per-bar flags above. Hundreds of sub-tolerance same-sign days compound into a slow bias the per-day check never
      trips. Judged on a staircase over the shared window — how far our cumulative return diverges from the same
      vendor's own, relatively (ratio) AND absolutely (cumulative-return points); the bigger the absolute gap, the
      lower the relative bar (never annualized). Split-only drift should be ~0; a non-zero total-return drift means a
      dropped/under-counted dividend stream vs the vendor's adjClose.
    </p>
    <p>
      <span className="text-gray-700 dark:text-gray-300">Drift regime</span> — expand a drift row (or run a single
      ticker) to see WHERE the cumulative drift accumulated. <span className="text-red-500">Concentrated</span> — a few
      dates carry it, so a phantom/mis-typed corporate action or a handful of mis-stated dividends is the prime suspect
      (open those dates). <span className="text-purple-400">Dividend-wide</span> — spread, but clustered on dividend
      ex-dates beyond their fair share, i.e. systematic dividend mishandling. <span className="text-gray-400">Diffuse</span> —
      spread evenly with dividends not over-represented, i.e. benign vendor convention noise (or one orphan raw bar).
      Each contributor date is tagged <span className="text-purple-400">Div</span> / <span className="text-blue-400">Action</span> /
      <span className="text-gray-400"> Orphan</span>.
    </p>
    <p>
      Impossible-gap “In vendor?” cross-check: <span className="text-green-500">In both</span> — the vendor saw
      the same gap (advisory only — a shared error like a spinoff-mishandled-as-split shows in both yet both are
      still wrong, so check the tag). <span className="text-red-500">Ours only</span> — the vendor has data here
      but no gap, so it's likely our mechanical residual. <span className="text-gray-400">Vendor n/a</span> — no
      vendor bar on that date (incomplete legacy history), can't cross-check.
    </p>
  </div>
);

export default function PriceIntegrityPage() {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;

  // ── Universe scan (default view) ──────────────────────────────────────────
  const [scan, setScan] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [rescanMsg, setRescanMsg] = useState(null);
  const [scanScope, setScanScope] = useState('active');

  const loadScan = useCallback(async () => {
    setScanLoading(true);
    setScanError(null);
    try {
      const { data } = await tradingService.getPriceContinuityScan(scanScope);
      setScan(data);
    } catch (err) {
      setScanError('Could not load the universe scan. Try refresh or check server logs.');
    } finally {
      setScanLoading(false);
    }
  }, [scanScope]);

  useEffect(() => {
    loadScan();
  }, [loadScan]);

  const doRescan = async () => {
    setRescanMsg(null);
    try {
      await tradingService.triggerPriceContinuityScan(scanScope);
      setRescanMsg(scopeMeta(scanScope).rescanMsg);
    } catch (err) {
      const status = err?.response?.status;
      setRescanMsg(
        status === 403
          ? 'Only an admin can trigger a rescan.'
          : status === 429
          ? 'Rescan rate limit hit — try again later.'
          : 'Could not queue the rescan. Check server logs.'
      );
    }
  };

  const scanFlags = scan?.flags || [];
  const scanTr = scan?.total_return || null;
  const scanTrFlags = scanTr?.flags || [];
  const neverScanned = scan?.status === 'never';

  // ── Single-ticker review (drill-down) ─────────────────────────────────────
  const [ticker, setTicker] = useState('');
  const [start, setStart] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);

  const runReview = async () => {
    const t = ticker.trim().toUpperCase();
    if (!t) return;
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      const { data } = await tradingService.reviewPriceContinuity(t, start.trim());
      setReport(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setError(
        detail ||
          (status === 404
            ? `${t} is not on the security spine.`
            : 'Continuity review failed. Try again or check server logs.')
      );
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') runReview();
  };

  const flags = report?.flags || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-serif text-gray-900 dark:text-gray-100">Price Integrity</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Two comparisons against the vendor: <span className="text-gray-700 dark:text-gray-300">Split-adjusted</span>{' '}
          (our split-only series vs the vendor close) and <span className="text-gray-700 dark:text-gray-300">Total return</span>{' '}
          (our split+dividend series vs the vendor adjClose), each with a divergence check and an impossible-gap
          backstop. Read-only; flags suspect names for triage.
        </p>
      </div>

      {/* ── UNIVERSE SCAN (collapsed by default; user expands) ──────────── */}
      <Collapsible title="Universe scan" accent="text-gray-900 dark:text-gray-100" defaultOpen={false}>
        <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">
              {scan && !neverScanned
                ? `Last scanned ${fmtTs(scan.generated_at)}`
                : scopeMeta(scanScope).subtitle}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ScopeToggle scope={scanScope} onChange={setScanScope} disabled={scanLoading} />
            <button
              onClick={loadScan}
              disabled={scanLoading}
              className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60 transition-colors disabled:opacity-40"
            >
              {scanLoading ? 'Loading…' : 'Refresh'}
            </button>
            {isAdmin && (
              <button
                onClick={doRescan}
                className="px-3 py-1.5 rounded-lg bg-brand-gold/15 border border-brand-gold/40 text-brand-gold text-xs font-medium hover:bg-brand-gold/25 transition-colors"
              >
                Rescan universe
              </button>
            )}
          </div>
        </div>

        {rescanMsg && (
          <div className="rounded-lg border border-brand-gold/30 bg-brand-gold/10 px-3 py-2 text-xs text-brand-gold">
            {rescanMsg}
          </div>
        )}

        <ScanProgress progress={scan?.progress} />

        {scanError && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
            {scanError}
          </div>
        )}

        {neverScanned && !scanError && (
          <div className="rounded-lg border border-gray-200 dark:border-gray-800 px-4 py-6 text-center text-xs text-gray-500 dark:text-gray-400">
            No universe scan yet.{' '}
            {isAdmin ? 'Hit “Rescan universe” to build the first snapshot.' : 'Ask an admin to run the first scan.'}
          </div>
        )}

        {scan && !neverScanned && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <StatTile label="Scanned" value={scan.n_scanned ?? 0} />
              <StatTile
                label="Split flagged"
                value={scan.n_flagged ?? 0}
                accent={(scan.n_flagged ?? 0) > 0 ? 'text-amber-500' : 'text-green-500'}
              />
              <StatTile label="Split flags" value={scan.n_total_flags ?? 0} />
              {scanTr ? (
                <>
                  <StatTile
                    label="TR flagged"
                    value={scanTr.n_flagged ?? 0}
                    accent={(scanTr.n_flagged ?? 0) > 0 ? 'text-amber-500' : 'text-green-500'}
                  />
                  <StatTile label="TR flags" value={scanTr.n_total_flags ?? 0} />
                </>
              ) : (
                <>
                  <StatTile label="Clean" value={scan.n_clean ?? 0} accent="text-green-500" />
                  <StatTile label="Universe" value={scan.n_securities ?? 0} />
                </>
              )}
              <StatTile
                label={scanTr ? 'TR drift' : 'Drift'}
                value={(scanTr ? scanTr.n_drift : scan.n_drift) ?? 0}
                accent={((scanTr ? scanTr.n_drift : scan.n_drift) ?? 0) > 0 ? 'text-amber-500' : 'text-green-500'}
              />
              <StatTile
                label="Errors"
                value={scan.n_errors ?? 0}
                accent={(scan.n_errors ?? 0) > 0 ? 'text-red-500' : undefined}
              />
            </div>

            <ComparisonModules
              splitFlags={scanFlags}
              trFlags={scanTrFlags}
              trAvailable={!!scanTr}
              showTicker
            />

            <Collapsible
              title="Cumulative drift"
              count={(scanTr ? scanTr.n_drift : scan.n_drift) ?? 0}
              accent="text-gray-900 dark:text-gray-100"
              defaultOpen={((scanTr ? scanTr.n_drift : scan.n_drift) ?? 0) > 0}
              badge={
                <CleanBadge clean={((scanTr ? scanTr.n_drift : scan.n_drift) ?? 0) === 0} />
              }
            >
              <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                The third axis: slow same-sign bias hundreds of sub-tolerance days compound into, which the per-day
                divergence check never trips. Split drift ~0 (both split-adjusted); a non-zero total-return drift is a
                dropped/under-counted dividend stream vs the vendor's adjClose. Sorted worst first.
              </div>
              <DriftTable securities={scan.securities} trAvailable={!!scanTr} />
            </Collapsible>

            {(scan.errors?.length ?? 0) > 0 && (
              <details className="text-[11px] text-gray-500 dark:text-gray-400">
                <summary className="cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                  {scan.errors.length} name{scan.errors.length === 1 ? '' : 's'} skipped (off-spine / no data)
                </summary>
                <div className="mt-1 font-mono break-words">
                  {scan.errors.map((e) => `${e.ticker} (${e.reason})`).join(', ')}
                </div>
              </details>
            )}
          </div>
        )}
        </div>
      </Collapsible>

      {/* ── CORRECTIONS (correction scan — expanded by default) ──────────── */}
      <CorrectionsSection isAdmin={isAdmin} />

      {/* ── SINGLE-TICKER REVIEW (collapsed by default; user expands) ───── */}
      <Collapsible title="Check one ticker" accent="text-gray-900 dark:text-gray-100" defaultOpen={false}>
        <div className="space-y-4 pt-1">
        <div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
            Live drill-down for a single name (one vendor fetch).
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1">
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
              Ticker
            </label>
            <input
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="e.g. AAPL"
              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 font-mono uppercase placeholder:normal-case focus:outline-none focus:border-brand-gold/60"
            />
          </div>
          <div className="w-full sm:w-40">
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
              Start (optional)
            </label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full px-3 py-2 rounded-lg bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-brand-gold/60"
            />
          </div>
          <button
            onClick={runReview}
            disabled={loading || !ticker.trim()}
            className="px-4 py-2 rounded-lg bg-brand-gold/15 border border-brand-gold/40 text-brand-gold text-sm font-medium hover:bg-brand-gold/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {loading ? 'Checking…' : 'Recheck'}
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
            {error}
          </div>
        )}

        {report && (
          <div className="space-y-4">
            <div
              className={`rounded-lg border px-4 py-3 flex items-center justify-between ${
                report.clean ? 'border-green-500/30 bg-green-500/10' : 'border-amber-500/30 bg-amber-500/10'
              }`}
            >
              <div>
                <div className={`text-sm font-medium ${report.clean ? 'text-green-500' : 'text-amber-500'}`}>
                  {report.clean ? 'Clean — no flags' : `${flags.length} flag${flags.length === 1 ? '' : 's'} to review`}
                </div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  {report.ticker} · security #{report.security_id} · as of {fmtDate(report.as_of)}
                </div>
              </div>
              <div className="text-right text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                {report.n_common} common days
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <StatTile label="Our bars" value={report.n_our_bars} />
              <StatTile label="Vendor bars" value={report.n_vendor_bars} />
              <StatTile label="Common" value={report.n_common} />
              <StatTile label="Action ex-dates" value={report.n_action_ex_dates} />
              <StatTile
                label="Split flags"
                value={report.flags?.length ?? 0}
                accent={(report.flags?.length ?? 0) > 0 ? 'text-amber-500' : 'text-green-500'}
              />
              <StatTile
                label="TR flags"
                value={report.total_return?.flags?.length ?? 0}
                accent={(report.total_return?.flags?.length ?? 0) > 0 ? 'text-amber-500' : 'text-green-500'}
              />
            </div>

            <ComparisonModules
              splitFlags={flags}
              trFlags={report.total_return?.flags || []}
              trAvailable={!!report.total_return}
            />

            <DriftModule
              split={report.cumulative_drift}
              tr={report.total_return?.cumulative_drift}
              splitAttr={report.drift_attribution}
              trAttr={report.total_return?.drift_attribution}
              trAvailable={!!report.total_return}
            />
          </div>
        )}
        </div>
      </Collapsible>

      <Legend />
    </div>
  );
}
